import { useMeetingBooker } from '@/container/demo-flow/meeting-booker/hooks/useMeetingBooker';
// Shared runtime-fallback hook with onboarding-v2's CompletionStep — keeps the
// SLG flow aligned: if the country-config resolves to `demo` but the HubSpot
// meeting probe surfaces no slots / an upstream error / a malformed URL, the
// experience is rewritten to `request-demo` so users land on the SDR
// lead-capture form instead of a dead calendar. The hook also emits
// `meeting_fallback_to_request_demo` analytics internally.
import { useResolvedOnboardingExperience } from '@/features/onboarding-v2/components/personalization/steps/hooks/useResolvedOnboardingExperience';
import { getPhoneNumberVariantForCountry } from '@/utils/country-config';
import { markDemoBooked } from '@/utils/onboardingDemoState';
import { type CountryType } from '@heidi/utils/countries';
import {
  getAvailableDisplayLanguagesForCountry,
  shouldShowLanguageDropdown,
} from '@heidi/utils/i18n';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { parsePhoneNumber } from 'react-phone-number-input';

import { useBookDemoCalendarAnalytics } from '@/models/analytics/features/useBookDemoCalendarAnalytics';
import {
  deriveNameFromEmail,
  formatSlotDateTime,
  getBookingErrorMessage,
} from './book-demo-calendar-step.utils';
import { BookDemoMeetingVariant } from './book-demo-meeting-variant';
import { BookDemoRequestDemoVariant } from './book-demo-request-demo-variant';
import { BookDemoWebinarVariant } from './book-demo-webinar-variant';

interface BookDemoCalendarStepProps {
  email: string;
  initialCountry: CountryType | null;
  onBooked: () => void;
  onBack: () => void;
  onSignup: () => void;
}

export function BookDemoCalendarStep({
  email,
  initialCountry,
  onBooked,
  onBack,
  onSignup,
}: BookDemoCalendarStepProps) {
  const intl = useIntl();

  const [country, setCountry] = useState<CountryType | null>(initialCountry);
  const [languageCode, setLanguageCode] = useState<string | null>(() => {
    if (!initialCountry?.countryCode) return null;
    const [primary] = getAvailableDisplayLanguagesForCountry(initialCountry.countryCode);
    return primary ?? null;
  });
  const [bookingError, setBookingError] = useState<string>();
  const [phoneNumber, setPhoneNumber] = useState('');

  const { experience } = useResolvedOnboardingExperience({
    countryCode: country?.countryCode,
    countryName: country?.name,
    languageCode,
  });
  const meetingUrl = experience.type === 'demo' ? experience.meetingUrl : null;

  const meeting = useMeetingBooker(meetingUrl ?? '');

  const phoneNumberVariant = getPhoneNumberVariantForCountry(country?.countryCode);
  const showPhoneNumber = phoneNumberVariant !== 'hidden';
  const isPhoneNumberOptional = phoneNumberVariant === 'optional';

  const hasCountryPhoneMismatch = useMemo(() => {
    if (!country || !phoneNumber || !showPhoneNumber) return false;
    try {
      const parsed = parsePhoneNumber(phoneNumber);
      if (parsed?.country) return parsed.country !== country.countryCode;
    } catch {
      return false;
    }
    return false;
  }, [country, phoneNumber, showPhoneNumber]);

  const showLanguage = shouldShowLanguageDropdown(country?.countryCode);
  const availableLanguages = useMemo(
    () => getAvailableDisplayLanguagesForCountry(country?.countryCode),
    [country?.countryCode]
  );

  const {
    trackLanguageSelected,
    trackSlotSelected,
    trackSdrDemoRequested,
    trackWebinarSubmitted,
    bookWithAnalytics,
  } = useBookDemoCalendarAnalytics({
    countryCode: country?.countryCode,
    language: languageCode,
    meetingUrl,
  });

  const handleCountryChange = useCallback(
    (value: CountryType) => {
      setCountry(value);
      const [primary] = getAvailableDisplayLanguagesForCountry(value.countryCode);
      setLanguageCode(primary ?? null);
      // Drop any previously-entered phone so we never leak a stale value
      // into a country whose variant is `hidden`.
      setPhoneNumber('');
      meeting.clearSelection();
    },
    [meeting]
  );

  const handleLanguageChange = useCallback(
    (value: string) => {
      setLanguageCode(value);
      meeting.clearSelection();
      trackLanguageSelected(value);
    },
    [meeting, trackLanguageSelected]
  );

  const handleSlotSelect = useCallback(
    (slot: Parameters<typeof meeting.handleSelectSlot>[0]) => {
      meeting.handleSelectSlot(slot);
      if (slot) trackSlotSelected(meeting.activeDuration);
    },
    [meeting, trackSlotSelected]
  );

  const handleContinue = useCallback(async () => {
    if (!meeting.selectedSlot || !email.trim()) return;
    if (showPhoneNumber && !isPhoneNumberOptional && !phoneNumber) return;
    setBookingError(undefined);
    const { firstName, lastName } = deriveNameFromEmail(email);
    const result = await bookWithAnalytics(meeting.selectedSlot.startMillisUtc, () =>
      meeting.handleBook(firstName, lastName, email, {
        company: '-',
        ...(showPhoneNumber && phoneNumber && { phone: phoneNumber }),
      })
    );
    if (result.ok) {
      markDemoBooked();
      onBooked();
    } else setBookingError(getBookingErrorMessage(result.error, intl));
  }, [
    meeting,
    email,
    onBooked,
    intl,
    bookWithAnalytics,
    phoneNumber,
    showPhoneNumber,
    isPhoneNumberOptional,
  ]);

  if (country && experience.type === 'request-demo') {
    return (
      <BookDemoRequestDemoVariant
        email={email}
        country={country}
        onCountryChange={handleCountryChange}
        languageCode={languageCode}
        onLanguageChange={handleLanguageChange}
        showLanguage={showLanguage}
        availableLanguages={availableLanguages}
        onSubmitted={() => {
          markDemoBooked();
          onBooked();
        }}
        onBack={onBack}
        onTrackSubmitted={trackSdrDemoRequested}
      />
    );
  }

  if (country && experience.type === 'webinar') {
    return (
      <BookDemoWebinarVariant
        email={email}
        region={experience.region}
        country={country}
        onCountryChange={handleCountryChange}
        languageCode={languageCode}
        onLanguageChange={handleLanguageChange}
        showLanguage={showLanguage}
        availableLanguages={availableLanguages}
        onSubmitted={() => {
          markDemoBooked();
          onBooked();
        }}
        onBack={onBack}
        onTrackSubmitted={trackWebinarSubmitted}
      />
    );
  }

  const selectedDateTimeLabel = formatSlotDateTime(
    intl,
    meeting.selectedSlot?.startMillisUtc,
    meeting.timezone
  );
  const phoneRequiredButMissing =
    showPhoneNumber && !isPhoneNumberOptional && !phoneNumber;

  return (
    <BookDemoMeetingVariant
      email={email}
      country={country}
      onCountryChange={handleCountryChange}
      languageCode={languageCode}
      onLanguageChange={handleLanguageChange}
      showLanguage={showLanguage}
      availableLanguages={availableLanguages}
      phoneNumber={phoneNumber}
      onPhoneNumberChange={setPhoneNumber}
      showPhoneNumber={showPhoneNumber}
      isPhoneNumberOptional={isPhoneNumberOptional}
      hasCountryPhoneMismatch={hasCountryPhoneMismatch}
      phoneRequiredButMissing={phoneRequiredButMissing}
      meeting={meeting}
      meetingUrl={meetingUrl}
      selectedDateTimeLabel={selectedDateTimeLabel}
      bookingError={bookingError}
      onSelectSlot={handleSlotSelect}
      onContinue={() => void handleContinue()}
      onBack={onBack}
      onSignup={onSignup}
    />
  );
}
